import { MediaList } from "./components/MediaList";
import { Nav, regexIsNodeContentScene } from "./components/Nav";
import { MediaDetailContainer } from "./components/MediaDetail";
import { Actions } from "./components/Actions";
import { useContext, useEffect, useRef, useState, useMemo } from "react";
import { shallowEqual } from "react-redux";
import type { AppDispatch, RootState } from "../../common/state/store";
import type { IMedia } from "../../model/unityObject";
import {
  setCurrentMediaSidebar,
  getUploadedFile,
  setUploadedFile,
  setUploadedOldFile,
  getCurrentModeListGallery,
  fetchMediaSearchIndex,
  fetchMediaTags,
  getFilteredMedias,
  fetchFilteredMedias,
  getMediaSearch,
  getMediaSearchIndex,
} from "./state/gallerySlice";
import { currentOrgSelector } from "../../common/state/selectors/authSelector";
import {
  closeConfirmationModal,
  openConfirmationModal,
} from "../../common/state/slice/modal/modalSlice";
import ModalConfirmation from "../../common/components/Modal/ModalConfirmation";
import { useTranslation } from "react-i18next";
import { GenerateMediaPanel } from "./components/GenerateMediaPanel";
import { UploadPanel } from "./components/UploadPanel";
import { getNodeById } from "../graph/state/graphLegacy";
import { useLocation } from "react-router-dom";
import {
  computeUploadPanelParams,
  fromMediaTypeInferInputAcceptParam,
  validateIncomingMediaFile,
} from "./state/uploadUtil";
import { setAlert } from "../alert/state/alertsSlice";
import { FolderDownloadIcon } from "@heroicons/react/outline";
import { PaginationControls } from "src/common/components/PaginationControls/PaginationControls";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { LoadingState } from "src/common/state/slice/appStateSlice";
import { IsLoading, useLoading } from "src/common/components/AppState/IsLoading";
import { useGalleryPagination } from "./useGalleryPagination";
import { isGifMedia } from "./util/mediaTypeUtil";
import { hasBeenReplaced } from "./util/isDerivedMediaUrlStale";
import { InsideGalleryContext } from "./picker/insideGalleryContext";
import type { ConstraintCheck, MediaPickerConstraints } from "./picker/mediaPickerConstraints";
import { checkMediaAgainstConstraints } from "./picker/mediaPickerConstraints";

export const MEDIA_LIST_COMPONENT_ID = "mediaListComponent";

const PICKER_NODE_KEY = "picker";
const MAX_SCROLL_TO_SELECTION_ATTEMPTS = 20;

export interface GalleryPickerConfig {
  constraints: MediaPickerConstraints;
  uploadAccept?: string;
  initialMediaId: number;
  graphNode?: { nodeId: string; contentType?: string };
  title?: string;
  acceptsRawFile: boolean;
  onConfirm: (media: IMedia) => void;
  onRawFile: (file: File) => void;
  onCancel: () => void;
  registerCancel?: (cancel: () => void) => void;
}

export const Gallery = ({ picker }: { picker?: GalleryPickerConfig }) => {
  const isPicker = Boolean(picker);
  const nodeId = picker ? picker.graphNode?.nodeId ?? PICKER_NODE_KEY : undefined;
  const contentType = picker?.graphNode?.contentType;
  const title = picker?.title ?? "";
  const allowedMediaTypeOverride = picker?.constraints.allowedMediaTypes;
  const allowedMedia360TagOverride = picker?.constraints.required360Tag;
  const uploadAcceptOverride = picker?.uploadAccept;
  const onUploadedFileSelect = picker?.acceptsRawFile ? picker.onRawFile : undefined;
  const originalMediaId = picker?.initialMediaId ?? 0;

  const { t } = useTranslation();
  const dispatch: AppDispatch = useAppDispatch();
  const handlePageChange = useGalleryPagination();
  const { pathname } = useLocation();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const navbarRef = useRef<HTMLDivElement>(null);

  const nodeTypeScene = useAppSelector((state: RootState) =>
    getNodeById(state, String(nodeId), contentType),
  );

  const sourceMediasIndex = useAppSelector(getMediaSearchIndex);
  const { page, limit } = useAppSelector(getMediaSearch);

  const currentNode = useAppSelector((state: RootState) =>
    Number(nodeId) !== 0 ? getNodeById(state, nodeId ? nodeId : "-1") : null,
  );
  const isNodeContentScene = currentNode?.modelName === "content-scene";
  const isContentSceneContext = regexIsNodeContentScene(pathname) || isNodeContentScene;

  const filteredSourceMediasIndex = useMemo(
    () =>
      isContentSceneContext
        ? sourceMediasIndex.filter((media) => !isGifMedia(media))
        : sourceMediasIndex,
    [sourceMediasIndex, isContentSceneContext],
  );

  const totalSourceMedias = filteredSourceMediasIndex.length;

  const totalPages = Math.max(1, Math.ceil(totalSourceMedias / (limit ?? 20)));

  const [isDragging, setIsDragging] = useState(false);
  const [opacity, setOpacity] = useState("opacity-0");

  const [selectedMediaId, setSelectedMediaId] = useState<number>(originalMediaId);
  const [detailOpen, setDetailOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);

  const { filteredMedias, org } = useAppSelector(
    (state: RootState) => ({
      filteredMedias: getFilteredMedias(state),
      org: currentOrgSelector(state),
    }),
    shallowEqual,
  );

  useEffect(() => {
    if (org?.id === undefined) return;
    void dispatch(fetchFilteredMedias({ limit: 20, componentId: MEDIA_LIST_COMPONENT_ID }));
    void dispatch(fetchMediaTags({ orgId: Number(org.id) }));
  }, [org?.id]);

  const currentModeListGallery = useAppSelector(getCurrentModeListGallery);
  const uploading = useAppSelector(getUploadedFile);
  const isNestedInGallery = useContext(InsideGalleryContext);
  const currentMediaSearch = useAppSelector(getMediaSearch, shallowEqual);
  const galleryLoadingState = useLoading(MEDIA_LIST_COMPONENT_ID);
  const isGalleryLoading = galleryLoadingState !== LoadingState.Success;

  const openMediaDetail = (mediaId: number) => {
    setDetailOpen(true);
    dispatch(setCurrentMediaSidebar(mediaId));
  };

  useEffect(() => {
    dispatch(setUploadedFile(undefined));
    dispatch(setUploadedOldFile(undefined));
    if (!isPicker) {
      void dispatch(fetchMediaSearchIndex({}));
    }
  }, [isPicker]);

  const uploadingAtMount = useRef(uploading);
  useEffect(() => {
    if (uploading && uploading !== uploadingAtMount.current) {
      setUploadOpen(true);
    }
  }, [uploading]);

  useEffect(() => {
    if (wrapperRef && wrapperRef.current) {
      wrapperRef.current.scrollIntoView();
    }
  }, [currentMediaSearch]);

  useEffect(() => {
    if (originalMediaId <= 0) return undefined;
    let attempts = 0;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const scrollToSelection = () => {
      const el = document.getElementById("gallery-selected-media-target");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      } else if (attempts < MAX_SCROLL_TO_SELECTION_ATTEMPTS) {
        attempts += 1;
        timeout = setTimeout(scrollToSelection, 100);
      }
    };
    scrollToSelection();
    return () => clearTimeout(timeout);
  }, [originalMediaId]);

  const onCancelWrapper = () => {
    if (!picker) return;
    if (Number(originalMediaId) !== Number(selectedMediaId)) {
      dispatch(openConfirmationModal("confirmCloseGallery"));
    } else {
      dispatch(closeConfirmationModal("confirmCloseGallery"));
      picker.onCancel();
    }
  };

  const onCancelRef = useRef(onCancelWrapper);
  onCancelRef.current = onCancelWrapper;
  const registerCancel = picker?.registerCancel;
  useEffect(() => {
    registerCancel?.(() => onCancelRef.current());
  }, [registerCancel]);

  const alertConstraintFailure = (check: ConstraintCheck) => {
    if (!("reason" in check)) return;
    dispatch(
      setAlert({
        isOpen: true,
        type: "danger",
        msg: t(check.reason === "type" ? "error.invalidMediaType" : "error.invalid360Tag", {
          expected: check.expected,
          actual: check.actual,
        }),
      }),
    );
  };

  const handleConfirm = (media: IMedia) => {
    if (!picker) return;
    const check = checkMediaAgainstConstraints(media, picker.constraints);
    if (!check.ok) {
      alertConstraintFailure(check);
      return;
    }
    picker.onConfirm(media);
  };

  const handleMediaCreated = (media: IMedia) => {
    if (picker) {
      const check = checkMediaAgainstConstraints(media, picker.constraints);
      if (check.ok) {
        picker.onConfirm(media);
        return;
      }
      alertConstraintFailure(check);
    }
    setSelectedMediaId(Number(media.id));
  };

  const closeUploadPanel = () => setUploadOpen(false);

  const isHiddenTranscodingMedia = (media: IMedia) =>
    media.upload_status === "transcoding" && !hasBeenReplaced(media);

  const getFilteredMediaForContext = () => {
    if (isContentSceneContext) {
      return filteredMedias.filter(
        (media: IMedia) => !isHiddenTranscodingMedia(media) && !isGifMedia(media),
      );
    } else {
      return filteredMedias.filter((media: IMedia) => !isHiddenTranscodingMedia(media));
    }
  };

  const dynamicWarningDragAndDrop = () => {
    if (!nodeId) {
      return t("general.extensionNotAccepted");
    }

    switch (nodeTypeScene?.modelName) {
      case "content-video-2-d":
        return t("general.extensionNotAcceptedVideo2d");
      case "content-audio":
        return t("general.extensionNotAcceptedAudio");
      case "content-scene":
        return currentMediaSearch?.mediaType === "model3d"
          ? t("general.extensionNotAccepted3d")
          : t("general.extensionNotAcceptedImageAndVideo");
      case "content-thumbnail":
      case "content-onboarding":
        return t("general.extensionNotAcceptedImage");
      case "content-3d-viewer":
      case "content-3-d-viewer":
        return t("general.extensionNotAccepted3d");
      default:
        return t("general.extensionNotAccepted");
    }
  };

  const alertExtension = () => {
    dispatch(
      setAlert({
        msg: dynamicWarningDragAndDrop(),
        type: "warning",
        isOpen: true,
        customTimeoutInMs: 20000,
      }),
    );
  };

  let selectedFileType = "all";
  if (currentNode) {
    if (currentNode.modelName === "content-scene" && currentMediaSearch?.mediaType !== "model3d") {
      selectedFileType = "content-scene";
    } else {
      selectedFileType = currentMediaSearch?.mediaType;
    }
  }
  if (regexIsNodeContentScene(pathname)) {
    selectedFileType = "content-scene";
  }

  const isNodeContentSceneOrRegex = isNodeContentScene || regexIsNodeContentScene(pathname);

  const isExternalFileDrag = (event: React.DragEvent) => {
    if (!event.dataTransfer) return false;
    return Array.prototype.some.call(
      event.dataTransfer.types,
      (type: string) => type === "Files" || type === "application/x-moz-file",
    );
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isExternalFileDrag(event)) return;
    setTimeout(() => {
      setOpacity("opacity-1");
    }, 300);
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setOpacity("opacity-0");
    setIsDragging(false);
  };

  const handleDragOver = (event: React.DragEvent) => {
    if (!isExternalFileDrag(event)) return;
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    setOpacity("opacity-0");

    if (!event.dataTransfer?.files?.length) return;

    const file = event.dataTransfer.files[0];
    const check = validateIncomingMediaFile(file, {
      accept: uploadAcceptOverride ?? fromMediaTypeInferInputAcceptParam(selectedFileType),
      isContentScene: isNodeContentSceneOrRegex,
    });

    if (!check.ok) {
      alertExtension();
    } else if (onUploadedFileSelect) {
      onUploadedFileSelect(file);
    } else {
      const cupp = computeUploadPanelParams({ file, from: "graph" });
      dispatch(setUploadedFile({ file, metadata: cupp }));
    }
  };

  return (
    <div>
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`absolute block flex w-full h-full ${
          isDragging
            ? `dragging bg-white ${opacity} ease-in-out duration-[900ms] border-2 border-green-500`
            : ""
        }`}
      >
        {/* hierarchy problem with divs and the draging is disabled when colliding with other divs */}
        {isDragging ? (
          <div className="pointer-events-none w-ful h-full mx-auto">
            <div className="pointer-events-none italic text-5xl text-white flex flex-col items-center mt-96 font-bold z-[999] text-gray-500 ">
              {t("general.dragFilesHere")}
              <div className="text-xl mt-2 text-gray-500">{t("general.dragAndDropText")}</div>

              <FolderDownloadIcon className="w-20 h-20 stroke-green-500 mt-6" />
            </div>
          </div>
        ) : (
          <div ref={wrapperRef} className="flex w-full h-full">
            <div className="w-full gallery h-full flex flex-col">
              <div ref={navbarRef}>
                <Nav
                  title={title}
                  nodeId={nodeId ?? "0"}
                  allowedMediaTypeOverride={allowedMediaTypeOverride}
                  allowedMedia360TagOverride={allowedMedia360TagOverride}
                  originalMediaId={originalMediaId}
                  hideGenerateButton={isPicker && isNestedInGallery}
                  uploadAcceptOverride={uploadAcceptOverride}
                  onUploadedFileSelect={onUploadedFileSelect}
                  isPicker={isPicker}
                  onOpenGenerate={() => setGenerateOpen(true)}
                />
              </div>

              <div
                className={`flex flex-col flex-grow overflow-y-auto h-full  mediaListWrapper ${
                  currentModeListGallery && !isPicker ? "p-2" : ""
                }`}
              >
                <IsLoading componentId={MEDIA_LIST_COMPONENT_ID} showSuccess={false}>
                  <MediaList
                    medias={getFilteredMediaForContext()}
                    isModalEdit={!isPicker}
                    listMode={isPicker ? false : currentModeListGallery}
                    openMediaDetail={openMediaDetail}
                    currentMedia={selectedMediaId}
                    onSelectMedia={setSelectedMediaId}
                    onOpenUpload={() => setUploadOpen(true)}
                    isMediaPickerMode={isPicker}
                    componentId={MEDIA_LIST_COMPONENT_ID}
                  />
                </IsLoading>
              </div>

              <ModalConfirmation
                title={t("pages.gallery.cancelConfirmationModal.title")}
                text={t("pages.gallery.cancelConfirmationModal.body")}
                onClickSubmit={() => {
                  dispatch(closeConfirmationModal("confirmCloseGallery"));
                  picker?.onCancel();
                }}
                id={"confirmCloseGallery"}
              />

              <IsLoading componentId={MEDIA_LIST_COMPONENT_ID} showSuccess={false}>
                <div className="h-[4rem] w-full border-t ">
                  <PaginationControls
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    isLoading={isGalleryLoading}
                    className="w-[97%] self-center"
                    galleryMode
                  />
                </div>
              </IsLoading>

              {picker && (
                <Actions
                  selectedMediaId={selectedMediaId}
                  onConfirm={handleConfirm}
                  onCancel={onCancelWrapper}
                />
              )}
            </div>

            <MediaDetailContainer
              open={detailOpen}
              onClose={() => setDetailOpen(false)}
              large={false}
            />
            <UploadPanel
              open={uploadOpen}
              onClose={closeUploadPanel}
              onMediaCreated={handleMediaCreated}
            />
            {!(isPicker && isNestedInGallery) && (
              <InsideGalleryContext.Provider value={true}>
                <GenerateMediaPanel
                  open={generateOpen}
                  onClose={() => setGenerateOpen(false)}
                  onMediaCreated={handleMediaCreated}
                />
              </InsideGalleryContext.Provider>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
